import React, { useEffect, useState } from 'react';
import styled from "styled-components";
import { HeadlineSmall } from "@shared/ui/atoms/fonts/Headline";
import IconButton from "@shared/ui/molecules/icon-button/IconButton";
import { IconTypes } from "@shared/ui/atoms/icon/Icon";
import {
  refreshBookings,
  selectedOffice,
  selectIsSidebarOpen,
  setOffice,
  setSidebarOpen
} from "@features/meeting-rooms/pages/models/room-finder.duck";
import isMobile from "is-mobile";
import { selectActiveDay, setActiveDay } from '@features/whereabouts/store/editMovements.duck';
import Calendar from "@shared/ui/molecules/calendar/Calendar";
import { DATE_FORMAT } from '@shared/utils/DateUtils';
import { addOrReplaceQueryParam } from '@shared/utils/UrlUtils';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { BodyRegular } from "@shared/ui/atoms/fonts/Body";
import { OfficeDropDownList } from "@shared/ui/organisms/OfficeDropDownList";
import { FloatingSidebar } from "@features/whereabouts/pages/company-movements/components/ViewComponents";
import { desktopSmall } from "@shared/ui/atoms/MediaQueries";
import { useDispatch, useSelector } from "react-redux";
import { selectAllRootOffices } from '@features/desks/store/advanceHotDeskingSetup.duck';
import moment, { Moment } from "moment";
import { OutlineButton } from "@shared/ui/atoms/buttons/OutlineButton";
import { isInTeams } from '@shared/utils/TeamsUtils';
import { LoadingSpinner } from "@shared/ui/atoms/LoadingSpinner";
import { loadDailyReport, loadMonthlyReport, loadWeeklyReport } from "./data/HospitalReports.http.ts";

enum TypeOfReport {
  DAILY, WEEKLY, MONTHLY,
}

export function HospitalitySidebar(props: Props) {
  const dispatch = useDispatch();
  const calendarDate = useSelector(selectActiveDay);
  const office = useSelector(selectedOffice);
  const allOfficesForMeetingRooms = useSelector(selectAllRootOffices);
  const isSideBarOpen = useSelector(selectIsSidebarOpen);
  const [reportLoading, setReportLoading] = useState(false);

  const onOfficeSelected = (office: OfficeEntity) => {
    dispatch(setOffice(office));
  }

  const onDateSelected = (date: Moment) => {
    addOrReplaceQueryParam('date', date.format(DATE_FORMAT))
    dispatch(setActiveDay(date));
    dispatch(refreshBookings())
  }

  useEffect(() => {
    if (isMobile()) {
      dispatch(setSidebarOpen(false));
    }
  }, [dispatch]);

  const onLoadReportClicked = async (type: TypeOfReport) => {
    if (calendarDate && office) {
      try {
        setReportLoading(true);
        if (type === TypeOfReport.DAILY) {
          await loadDailyReport(calendarDate, office);
        } else if (type === TypeOfReport.WEEKLY) {
          await loadWeeklyReport(calendarDate, office);
        } else if (type === TypeOfReport.MONTHLY) {
          await loadMonthlyReport(calendarDate, office);
        }
      } finally {
        setReportLoading(false);
      }
    }
  }

  return (
    <>
      {reportLoading && <LoadingSpinner fullScreen={true} message={'Compiling report'} />}

      <Sidebar className={'roomFinderView__sidebar roomFinderView__sidebar--open'} isInTeams={isInTeams()} isOpen={isSideBarOpen}>
        <HeadlineSmall className={'roomFinderSidebar__title'}>Filter rooms</HeadlineSmall>
        <IconButton icon={IconTypes.Close}
                    size={"large"} className={"sidebar__closeBtn roomFinderSidebar__closeBtn"}
                    onClick={() => dispatch(setSidebarOpen(false))} />
        <OfficeDropDownListWrapper>
          <BodyRegular weight={700} style={{marginBottom: 8}}>Choose an office</BodyRegular>
          <OfficeDropDownList onSelect={onOfficeSelected} offices={allOfficesForMeetingRooms} disabled={false} preSelectedOffice={office?.id} />
        </OfficeDropDownListWrapper>
        <Calendar onDateSelect={onDateSelected}
                  className={'calendar__roomFinderSidebar'}
                  initialDate={calendarDate ?? moment()}
                  activeDate={calendarDate} style={{margin: '0 auto 24px auto'}} />

        <OutlineButton text={"hospitality.report-day"}
                       click={() => onLoadReportClicked(TypeOfReport.DAILY)}
                       fullWidth={true}
                       style={{marginBottom: 16}}
                       disabled={reportLoading || !calendarDate || !office} />

        <OutlineButton text={"hospitality.report-week"}
                       click={() => onLoadReportClicked(TypeOfReport.WEEKLY)}
                       fullWidth={true}
                       style={{marginBottom: 16}}
                       disabled={reportLoading || !calendarDate || !office} />
        <OutlineButton text={"hospitality.report-month"}
                       click={() => onLoadReportClicked(TypeOfReport.MONTHLY)}
                       fullWidth={true}
                       style={{}}
                       disabled={reportLoading || !calendarDate || !office} />
      </Sidebar>
    </>
  )
}

interface Props {
}

const OfficeDropDownListWrapper = styled.div`
  margin: 0 0 20px 0;
  width: 100%;
`

const Sidebar = styled(FloatingSidebar)<any>`
  top: ${props => props.isInTeams ? '56px' : '114px'};
  height: ${props => props.isInTeams ? 'calc(100vh - 56px)' : 'calc(100vh - 114px)'};
  position: absolute;
  overflow: hidden;
  @media (${desktopSmall}) {
    .sidebar__closeBtn {
      display: none;
    }
  }
  
  display: ${props => props.isOpen ? 'block' : 'none'};
  @media (${desktopSmall}) {
    right: 0;
    display: block !important;
  }
  .roomFinderSidebar__closeBtn {
    top: 16px;
    right: 16px;
    position: absolute;
  }
`
